import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Youtube, Upload, X, Loader2, Check, AlertCircle, HelpCircle } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import logo from '../../assets/images/logo-artie.png';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const PLATFORMS = [
  { id: "youtube", label: "YouTube" },
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
  { id: "spotify", label: "Spotify" },
  { id: "apple_podcasts", label: "Apple Podcasts" },
];

export default function OnboardingStep2() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuthStore();
  const [showYouTubeDisclosure, setShowYouTubeDisclosure] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showManualUpload, setShowManualUpload] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState("youtube");
  const [isUploading, setIsUploading] = useState(false);

  // Group screenshots by platform
  const [platformData, setPlatformData] = useState<Record<string, {
    handle: string;
    follower_count?: number;
    avg_views?: number;
    core_performance?: any;
    engagement_retention?: any;
    traffic_discovery?: any;
    demographics?: any;
    screenshots: { url: string; key: string }[];
  }>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isYouTubeConnected = user?.social_profiles?.some(p => p.platform === 'youtube') || !!user?.youtube_data;
  const totalScreenshots = Object.values(platformData).reduce((sum, p) => sum + p.screenshots.length, 0);
  const hasMinimumUploads = totalScreenshots >= 2;
  const canContinue = isYouTubeConnected || hasMinimumUploads;

  const handleConnectYouTube = () => {
    setShowYouTubeDisclosure(true);
  };

  const handleYouTubeConnect = async () => {
    setIsConnecting(true);
    setShowYouTubeDisclosure(false);
    try {
      localStorage.setItem("oauth_return_path", "/onboarding/step2");
      const { url } = await apiClient.connectYouTube();
      window.location.href = url;
    } catch (error) {
      console.error(error);
      toast.error("Failed to initiate YouTube connection");
      setIsConnecting(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const initData = await apiClient.initScreenshotUpload(file.name, file.type || "image/jpeg");

      const formData = new FormData();
      Object.entries(initData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append("file", file);

      await fetch(initData.upload_url, {
        method: "POST",
        body: formData,
        mode: 'no-cors'
      });

      toast.info("Analyzing screenshot...");
      const result = await apiClient.completeScreenshotUpload(initData.s3_key, selectedPlatform);
      const data = result.data;

      const blobUrl = URL.createObjectURL(file);

      // Extract analytics data from OCR if available
      let extractedData: any = {
        platform: selectedPlatform,
        url: blobUrl,
        key: initData.s3_key
      };

      if (data) {
        const cleanNumber = (val: any) => {
          if (typeof val === 'number') return val;
          if (typeof val === 'string') {
            let cleaned = val.replace(/[,%\s]/g, '');
            let multiplier = 1;

            if (cleaned.toUpperCase().includes('K')) {
              multiplier = 1000;
              cleaned = cleaned.toUpperCase().replace('K', '');
            } else if (cleaned.toUpperCase().includes('M')) {
              multiplier = 1000000;
              cleaned = cleaned.toUpperCase().replace('M', '');
            } else if (cleaned.toUpperCase().includes('B')) {
              multiplier = 1000000000;
              cleaned = cleaned.toUpperCase().replace('B', '');
            }

            const num = Number(cleaned);
            return isNaN(num) ? undefined : num * multiplier;
          }
          return undefined;
        };

        // Extract handle/username
        if (data.username || data.channel_name) {
          extractedData.handle = data.username || data.channel_name;
        }

        // Extract follower/subscriber count
        if (data.followers_count || data.subscribers_count) {
          const val = cleanNumber(data.followers_count || data.subscribers_count);
          if (val) extractedData.follower_count = val;
        }

        // Extract views
        if (data.video_views || data.views_count) {
          const val = cleanNumber(data.video_views || data.views_count);
          if (val) extractedData.avg_views = val;
        }

        // Store rich analytics data
        if (data.core_performance) extractedData.core_performance = data.core_performance;
        if (data.engagement_retention) extractedData.engagement_retention = data.engagement_retention;
        if (data.traffic_discovery) extractedData.traffic_discovery = data.traffic_discovery;
        if (data.demographics) extractedData.demographics = data.demographics;
      }

      // Add screenshot to platform data
      setPlatformData(prev => {
        const existing = prev[selectedPlatform] || {
          handle: "",
          screenshots: [],
          follower_count: undefined,
          avg_views: undefined,
          core_performance: undefined,
          engagement_retention: undefined,
          traffic_discovery: undefined,
          demographics: undefined
        };

        return {
          ...prev,
          [selectedPlatform]: {
            handle: extractedData.handle || existing.handle || "",
            follower_count: extractedData.follower_count || existing.follower_count,
            avg_views: extractedData.avg_views || existing.avg_views,
            core_performance: extractedData.core_performance || existing.core_performance,
            engagement_retention: extractedData.engagement_retention || existing.engagement_retention,
            traffic_discovery: extractedData.traffic_discovery || existing.traffic_discovery,
            demographics: extractedData.demographics || existing.demographics,
            screenshots: [...existing.screenshots, { url: blobUrl, key: initData.s3_key }]
          }
        };
      });

      toast.success("Screenshot analyzed successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload screenshot");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveScreenshot = (platform: string, screenshotIndex: number) => {
    setPlatformData(prev => {
      const platformInfo = prev[platform];
      if (!platformInfo) return prev;

      const updatedScreenshots = platformInfo.screenshots.filter((_, i) => i !== screenshotIndex);

      // If no screenshots left, remove the platform entirely
      if (updatedScreenshots.length === 0) {
        const { [platform]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [platform]: {
          ...platformInfo,
          screenshots: updatedScreenshots
        }
      };
    });
    toast.success("Screenshot removed");
  };

  const handleUpdatePlatformHandle = (platform: string, handle: string) => {
    setPlatformData(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        handle
      }
    }));
  };

  const handleContinue = async () => {
    const hasYoutubeScreenshots = platformData.youtube && platformData.youtube.screenshots.length > 0;

    if (!isYouTubeConnected && !hasYoutubeScreenshots) {
      toast.error("Please connect your YouTube channel or upload screenshots for YouTube to continue.");
      return;
    }

    // Save platform data to user profile if any
    if (Object.keys(platformData).length > 0) {
      try {
        const currentProfiles = user?.social_profiles || [];
        const updatedProfiles = [...currentProfiles];

        for (const [platform, data] of Object.entries(platformData)) {
          const existingIndex = updatedProfiles.findIndex(p => p.platform === platform);
          const screenshotKeys = data.screenshots.map(s => s.key);

          if (existingIndex >= 0) {
            // Merge with existing profile
            const existing = updatedProfiles[existingIndex];
            updatedProfiles[existingIndex] = {
              ...existing,
              handle: data.handle || existing.handle || `${platform}_user`,
              follower_count: data.follower_count || existing.follower_count,
              avg_views: data.avg_views || existing.avg_views,
              screenshots: [...(existing.screenshots || []), ...screenshotKeys],
              core_performance: data.core_performance || existing.core_performance,
              engagement_retention: data.engagement_retention || existing.engagement_retention,
              traffic_discovery: data.traffic_discovery || existing.traffic_discovery,
              demographics: data.demographics || existing.demographics,
            };
          } else {
            // Create new profile with extracted data
            updatedProfiles.push({
              platform: platform as any,
              handle: data.handle || `${platform}_user`,
              follower_count: data.follower_count,
              avg_views: data.avg_views,
              screenshots: screenshotKeys,
              core_performance: data.core_performance,
              engagement_retention: data.engagement_retention,
              traffic_discovery: data.traffic_discovery,
              demographics: data.demographics,
            });
          }
        }

        // Filter out any profiles with empty handles to prevent validation errors
        const validProfiles = updatedProfiles.filter(profile =>
          profile.handle && profile.handle.trim().length > 0
        );

        await apiClient.updateCurrentUser({
          social_profiles: validProfiles,
          onboarding_step: 2
        });
        await checkAuth();
      } catch (error) {
        console.error("Failed to save screenshots", error);
        toast.error("Failed to save channel data");
      }
    }

    navigate("/onboarding/step3");
  };



  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center">

      {/* Top Navigation */}
      <nav className="w-full max-w-5xl px-6 py-6 flex items-center justify-end z-50 relative">
        <div className="flex items-center gap-4">
          <div className="flex gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
          </div>
          <span className="text-xs font-medium text-[oklch(0.55_0_0)]">Step 1 of 3</span>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 z-10 flex flex-col w-full max-w-2xl pt-8 pr-6 pb-32 pl-6 items-center">
        {/* Header */}
        <div className="animate-in slide-in-from-bottom-4 duration-500 flex flex-col mb-8 relative items-center">
          <div className="relative w-20 h-20 mb-4">
            <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] rounded-full blur-xl opacity-50"></div>
            <img
              src={logo}
              alt="Artie"
              className="w-full h-full rounded-full object-cover border-4 border-[oklch(1.0000_0_0)] shadow-xl relative z-10 animate-[float_3s_ease-in-out_infinite]"
            />
            <div className="-right-16 text-[oklch(1.0000_0_0)] transform animate-in slide-in-from-bottom-2 duration-500 delay-300 text-xs font-medium bg-orange-500 z-20 rounded-xl px-3 py-1.5 absolute top-0 shadow-lg rotate-2">
              Connect! 🔗
            </div>
          </div>
        </div>

        <div className="text-center mb-12 animate-in slide-in-from-bottom-4 duration-500">
          <h1 className="text-3xl md:text-4xl font-semibold font-cabinet tracking-tight text-[oklch(0.2686_0_0)] mb-3">
            Connect your content
          </h1>
          <p className="text-[oklch(0.55_0_0)] font-ibm text-sm md:text-base max-w-md mx-auto leading-relaxed">
            Link your YouTube channel or upload analytics screenshots to get your Artie Score.
          </p>
        </div>

        {/* YouTube Connect Section */}
        <div className="w-full mb-8 animate-in slide-in-from-bottom-4 duration-500 delay-100">
          <Card className="p-8 bg-white border border-[oklch(0.92_0_0)] rounded-2xl shadow-sm">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-md ring-1 ring-black/5">
                <Youtube className="w-8 h-8 text-[#FF0000]" />
              </div>

              <h3 className="text-xl font-semibold font-cabinet text-[oklch(0.2686_0_0)] mb-2">
                YouTube Integration
              </h3>
              <p className="text-[oklch(0.55_0_0)] font-ibm text-sm mb-6 max-w-sm">
                Connect your YouTube channel for automatic data import and accurate scoring.
              </p>

              <Button
                onClick={handleConnectYouTube}
                disabled={isConnecting || isYouTubeConnected}
                className="w-full sm:w-auto h-12 px-8 rounded-xl bg-[#FF0000] hover:bg-[#D90000] text-white shadow-lg"
              >
                {isYouTubeConnected ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Connected
                  </>
                ) : isConnecting ? (
                  "Connecting..."
                ) : (
                  <>
                    <Youtube className="w-5 h-5 mr-2" />
                    Connect YouTube
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>

        {/* Divider */}
        <div className="relative flex items-center w-full py-4">
          <div className="flex-grow border-t border-[oklch(0.92_0_0)]"></div>
          <span className="flex-shrink-0 mx-4 text-[oklch(0.55_0_0)] text-xs uppercase font-medium">Or</span>
          <div className="flex-grow border-t border-[oklch(0.92_0_0)]"></div>
        </div>

        {/* Manual Upload Section */}
        <div className="w-full animate-in slide-in-from-bottom-4 duration-500 delay-200">
          <Button
            variant="outline"
            onClick={() => setShowManualUpload(!showManualUpload)}
            className="w-full h-12 rounded-xl mb-4"
          >
            <Upload className="w-4 h-4 mr-2" />
            {showManualUpload ? "Hide Manual Upload" : "Upload Screenshots Instead"}
          </Button>

          {showManualUpload && (
            <Card className="p-6 bg-white border border-[oklch(0.92_0_0)] rounded-2xl shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <h3 className="text-sm font-semibold text-[oklch(0.2686_0_0)]">
                    Upload Analytics Screenshots
                  </h3>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-orange-500 hover:text-orange-600 transition-colors" title="See example screenshots">
                        <HelpCircle className="w-5 h-5 animate-bounce" />
                        <span className="sr-only">Learn more</span>
                      </button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Required Screenshots</DialogTitle>
                        <DialogDescription>
                          Please upload clear screenshots of your channel analytics.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="rounded-lg border bg-slate-50 p-4">
                          <div className="aspect-[16/9] bg-white rounded border border-slate-200 shadow-sm p-4 relative overflow-hidden mb-3">
                            {/* Mock Interface */}
                            <div className="w-1/3 h-4 bg-slate-100 rounded mb-4"></div>
                            <div className="flex gap-4 mb-6">
                              <div className="w-1/4 h-24 bg-blue-50 rounded border border-blue-100"></div>
                              <div className="w-1/4 h-24 bg-slate-50 rounded border border-slate-100"></div>
                              <div className="w-1/4 h-24 bg-slate-50 rounded border border-slate-100"></div>
                              <div className="w-1/4 h-24 bg-slate-50 rounded border border-slate-100"></div>
                            </div>
                            <div className="w-full h-32 bg-slate-50 rounded border border-slate-100 relative">
                              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-blue-50/50 to-transparent"></div>
                              <svg className="w-full h-full absolute bottom-0" preserveAspectRatio="none" viewBox="0 0 300 128">
                                <path d="M0,100 C100,50 200,80 300,40 L300,128 L0,128 Z" fill="rgba(59, 130, 246, 0.1)" />
                                <path d="M0,100 C100,50 200,80 300,40" stroke="rgba(59, 130, 246, 0.5)" strokeWidth="2" fill="none" />
                              </svg>
                            </div>

                            {/* Annotation */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/80 text-white text-xs px-2 py-1 rounded shadow-lg">
                              Overview Dashboard
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground text-center">
                            Example: YouTube Studio / Instagram Insights Dashboard
                          </p>
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium">What to include:</h4>
                          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                            <li>Key metrics (Views, Watch time, Subscribers/Followers)</li>
                            <li>Audience demographics if available</li>
                            <li>Recent content performance</li>
                          </ul>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <p className="text-xs text-[oklch(0.55_0_0)]">
                  Minimum 2 screenshots required
                </p>
              </div>

              {/* Platform Selection */}
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {PLATFORMS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPlatform(p.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedPlatform === p.id
                      ? "bg-[oklch(0.2686_0_0)] text-white"
                      : "bg-white border border-[oklch(0.92_0_0)] text-[oklch(0.55_0_0)] hover:border-[oklch(0.2686_0_0)]"
                      }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Upload Area */}
              <div
                onClick={() => !isUploading && fileInputRef.current?.click()}
                className="border-2 border-dashed border-[oklch(0.92_0_0)] rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-[oklch(0.97_0.01_60)] transition-all"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={isUploading}
                />

                {isUploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[oklch(0.70_0.18_55)] mb-2" />
                    <p className="text-sm font-medium text-[oklch(0.2686_0_0)]">Uploading & Analyzing...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-[oklch(0.70_0.18_55)] mb-2" />
                    <p className="text-sm font-medium text-[oklch(0.2686_0_0)] mb-1">
                      Click to upload {PLATFORMS.find(p => p.id === selectedPlatform)?.label} screenshot
                    </p>
                    <p className="text-xs text-[oklch(0.55_0_0)]">
                      Profile Overview & Analytics (Max 10MB)
                    </p>
                  </>
                )}
              </div>

              {/* Uploaded Screenshots - Grouped by Platform */}
              {Object.keys(platformData).length > 0 && (
                <div className="mt-4 space-y-4">
                  {Object.entries(platformData).map(([platform, data]) => (
                    <div key={platform} className="p-4 bg-white border border-[oklch(0.92_0_0)] rounded-xl">
                      {/* Platform Header */}
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-[oklch(0.2686_0_0)]">
                          {PLATFORMS.find(p => p.id === platform)?.label}
                        </span>
                        {data.handle && (
                          <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full border border-green-200">
                            Auto-detected
                          </span>
                        )}
                      </div>

                      {/* Handle Input */}
                      <div className="mb-3">
                        <label className="text-xs text-[oklch(0.55_0_0)] mb-1 block">
                          Handle / Username
                        </label>
                        <Input
                          value={data.handle || ""}
                          onChange={(e) => handleUpdatePlatformHandle(platform, e.target.value)}
                          placeholder="@username"
                          className="h-9 text-sm"
                        />
                      </div>

                      {/* Extracted Metrics */}
                      {(data.follower_count || data.avg_views) && (
                        <div className="flex gap-4 text-xs text-[oklch(0.55_0_0)] mb-3 pb-3 border-b">
                          {data.follower_count && (
                            <div>
                              <span className="font-medium">Followers:</span> {data.follower_count.toLocaleString()}
                            </div>
                          )}
                          {data.avg_views && (
                            <div>
                              <span className="font-medium">Views:</span> {data.avg_views.toLocaleString()}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Screenshots */}
                      <div>
                        <label className="text-xs text-[oklch(0.55_0_0)] mb-2 block">
                          Screenshots ({data.screenshots.length})
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {data.screenshots.map((screenshot, idx) => (
                            <div key={idx} className="relative w-20 h-20 rounded-lg border border-[oklch(0.92_0_0)] overflow-hidden group bg-white">
                              <img src={screenshot.url} alt="Screenshot" className="w-full h-full object-cover" />
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRemoveScreenshot(platform, idx); }}
                                className="absolute inset-0 bg-black/50 hover:bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Requirement Notice */}
              {totalScreenshots < 2 && (
                <div className="mt-4 bg-orange-50 border border-orange-100 rounded-xl p-3 flex gap-2 items-start">
                  <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-orange-900">
                      {totalScreenshots}/2 screenshots uploaded
                    </p>
                    <p className="text-xs text-orange-800/80">
                      Upload at least 2 screenshots to continue
                    </p>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </main>

      {/* Footer Actions */}
      <footer className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[oklch(1.0000_0_0)] via-[oklch(1.0000_0_0)] to-transparent z-50 flex justify-center">
        <div className="w-full max-w-2xl flex items-center justify-end gap-4">
          <Button
            onClick={handleContinue}
            disabled={!canContinue}
            className="px-8 py-3 rounded-xl bg-[oklch(0.2686_0_0)] hover:bg-[oklch(0.2686_0_0)]/90 text-white group"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </footer>

      {/* YouTube OAuth Disclosure Dialog */}
      <Dialog open={showYouTubeDisclosure} onOpenChange={setShowYouTubeDisclosure}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Youtube className="w-5 h-5 text-[#FF0000]" />
              YouTube Data Access
            </DialogTitle>
            <DialogDescription className="text-left space-y-3">
              <p>By connecting your YouTube account, you grant Artie permission to:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Access your channel statistics and analytics</li>
                <li>Read your video metadata and performance data</li>
                <li>Analyze your audience demographics and engagement metrics</li>
                <li>Generate insights based on your content performance</li>
              </ul>
              <p className="text-sm">We will only access data necessary for providing our services. You can disconnect your account at any time.</p>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowYouTubeDisclosure(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleYouTubeConnect}
              disabled={isConnecting}
              className="flex-1 bg-[#FF0000] hover:bg-[#D90000] text-white"
            >
              {isConnecting ? "Connecting..." : "Connect YouTube"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}